<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :showProviderFilter="true"
  >
    <ReportTable
      v-if="!loading && (prophyDataPerProvider.length || perioDataPerProvider.length || srpDataPerProvider.length)"
      title="Total"
      minWidth="1020px"
    >
      <thead>
        <tr>
          <th></th>
          <th><span>Gross production</span></th>
          <th><span>Writeoff</span></th>
          <th><span>Adjustment</span></th>
          <th><span>Net production</span></th>
          <th><span>Patient payments</span></th>
          <th><span>Insurance payments</span></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td></td>
          <td>{{ formatMoney(sumAll.grossProduction) }}</td>
          <td>{{ formatMoney(sumAll.writeoff) }}</td>
          <td>{{ formatMoney(sumAll.adjustment) }}</td>
          <td>{{ formatMoney(sumAll.netProduction) }}</td>
          <td>{{ formatMoney(sumAll.patientPayments) }}</td>
          <td>{{ formatMoney(sumAll.insurancePayments) }}</td>
        </tr>
      </tbody>
    </ReportTable>

    <ReportTable
      title="Prophy (D1110)"
      minWidth="1020px"
      className="hygiene-table"
    >
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th><span>Gross production</span></th>
          <th><span>Writeoff</span></th>
          <th><span>Adjustment</span></th>
          <th><span>Net production</span></th>
          <th><span>Patient payments</span></th>
          <th><span>Insurance payments</span></th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <tr v-if="prophyDataPerProvider.length">
          <td>Total</td>
          <td>{{ formatMoney(sumProphy.grossProduction) }}</td>
          <td>{{ formatMoney(sumProphy.writeoff) }}</td>
          <td>{{ formatMoney(sumProphy.adjustment) }}</td>
          <td>{{ formatMoney(sumProphy.netProduction) }}</td>
          <td>{{ formatMoney(sumProphy.patientPayments) }}</td>
          <td>{{ formatMoney(sumProphy.insurancePayments) }}</td>
        </tr>
        <tr
          v-for="(item, index) in prophyDataPerProvider"
          :key="index"
          :class="{
            active: item.isCurrentUserRecord,
            median: item.isMedian,
            hidden: !item.isActive || item.isHidden || item.isHiddenReport,
          }"
        >
          <td>{{ item.fullName }}</td>
          <td>{{ formatMoney(item.prophyGrossProduction) }}</td>
          <td>{{ formatMoney(item.prophyEstimatedWriteoff) }}</td>
          <td>{{ formatMoney(item.prophyCompletedProceduresAdjustment) }}</td>
          <td>
            {{ formatMoney(calculateNetProduction(
              item.prophyGrossProduction,
              item.prophyCompletedProceduresAdjustment,
              item.prophyEstimatedWriteoff)) }}
          </td>
          <td>{{ formatMoney(item.prophyPatientPayments) }}</td>
          <td>{{ formatMoney(item.prophyInsurancePayments) }}</td>
        </tr>

        <NoRecordsFound
          v-if="!prophyDataPerProvider.length && !loading"
          colspan="7"
        />
      </tbody>
    </ReportTable>

    <ReportTable
      title="Perio (D4910)"
      minWidth="1020px"
      className="hygiene-table"
    >
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th><span>Gross production</span></th>
          <th><span>Writeoff</span></th>
          <th><span>Adjustment</span></th>
          <th><span>Net production</span></th>
          <th><span>Patient payments</span></th>
          <th><span>Insurance payments</span></th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <tr v-if="perioDataPerProvider.length">
          <td>Total</td>
          <td>{{ formatMoney(sumPerio.grossProduction) }}</td>
          <td>{{ formatMoney(sumPerio.writeoff) }}</td>
          <td>{{ formatMoney(sumPerio.adjustment) }}</td>
          <td>{{ formatMoney(sumPerio.netProduction) }}</td>
          <td>{{ formatMoney(sumPerio.patientPayments) }}</td>
          <td>{{ formatMoney(sumPerio.insurancePayments) }}</td>
        </tr>
        <tr
          v-for="(item, index) in perioDataPerProvider"
          :key="index"
          :class="{
            active: item.isCurrentUserRecord,
            median: item.isMedian,
            hidden: !item.isActive || item.isHidden || item.isHiddenReport,
          }"
        >
          <td>{{ item.fullName }}</td>
          <td>{{ formatMoney(item.perioGrossProduction) }}</td>
          <td>{{ formatMoney(item.perioEstimatedWriteoff) }}</td>
          <td>{{ formatMoney(item.perioCompletedProceduresAdjustment) }}</td>
          <td>
            {{ formatMoney(calculateNetProduction(
              item.perioGrossProduction,
              item.perioCompletedProceduresAdjustment,
              item.perioEstimatedWriteoff)) }}
          </td>
          <td>{{ formatMoney(item.perioPatientPayments) }}</td>
          <td>{{ formatMoney(item.perioInsurancePayments) }}</td>
        </tr>

        <NoRecordsFound
          v-if="!perioDataPerProvider.length && !loading"
          colspan="7"
        />
      </tbody>
    </ReportTable>

    <ReportTable
      title="SRP (D4341, D4342, D4346)"
      minWidth="1020px"
      className="hygiene-table"
    >
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th><span>Gross production</span></th>
          <th><span>Writeoff</span></th>
          <th><span>Adjustment</span></th>
          <th><span>Net production</span></th>
          <th><span>Patient payments</span></th>
          <th><span>Insurance payments</span></th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <tr v-if="srpDataPerProviderAll.length">
          <td>Total</td>
          <td>{{ formatMoney(sumSrp.grossProduction) }}</td>
          <td>{{ formatMoney(sumSrp.writeoff) }}</td>
          <td>{{ formatMoney(sumSrp.adjustment) }}</td>
          <td>{{ formatMoney(sumSrp.netProduction) }}</td>
          <td>{{ formatMoney(sumSrp.patientPayments) }}</td>
          <td>{{ formatMoney(sumSrp.insurancePayments) }}</td>
        </tr>
        <tr
          v-for="(item, index) in srpDataPerProviderAll"
          :key="index"
          :class="{
            active: item.isCurrentUserRecord,
            median: item.isMedian,
            hidden: !item.isActive || item.isHidden || item.isHiddenReport,
          }"
        >
          <td>{{ item.fullName }}</td>
          <td>{{ formatMoney(item.srpGrossProduction) }}</td>
          <td>{{ formatMoney(item.srpEstimatedWriteoff) }}</td>
          <td>{{ formatMoney(item.srpCompletedProceduresAdjustment) }}</td>
          <td>
            {{ formatMoney(calculateNetProduction(
              item.srpGrossProduction,
              item.srpCompletedProceduresAdjustment,
              item.srpEstimatedWriteoff)) }}
          </td>
          <td>{{ formatMoney(item.srpPatientPayments) }}</td>
          <td>{{ formatMoney(item.srpInsurancePayments) }}</td>
        </tr>

        <NoRecordsFound
          v-if="!srpDataPerProviderAll.length && !loading"
          colspan="7"
        />
      </tbody>
    </ReportTable>
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import Utils from '../../../utils';

export default {
  name: 'ClinicHygiene',
  components: {
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      prophyDataPerProviderAll: [],
      perioDataPerProviderAll: [],
      srpDataPerProviderAll: [],
      loading: false,
    };
  },
  computed: {
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    prophyDataPerProvider() {
      return this.showHiddenProviders
        ? this.prophyDataPerProviderAll
        : this.prophyDataPerProviderAll.filter(this.providerIsVisible);
    },
    perioDataPerProvider() {
      return this.showHiddenProviders
        ? this.perioDataPerProviderAll
        : this.perioDataPerProviderAll.filter(this.providerIsVisible);
    },
    srpDataPerProvider() {
      return this.showHiddenProviders
        ? this.srpDataPerProviderAll
        : this.srpDataPerProviderAll.filter(this.providerIsVisible);
    },
    sumProphy() {
      return this.sumByProviders(this.prophyDataPerProviderAll, 'prophy');
    },
    sumPerio() {
      return this.sumByProviders(this.perioDataPerProviderAll, 'perio');
    },
    sumSrp() {
      return this.sumByProviders(this.srpDataPerProviderAll, 'srp');
    },
    sumAll() {
      return {
        patientPayments: this.sumByMetric('patientPayments', this.sumProphy, this.sumPerio, this.sumSrp),
        insurancePayments: this.sumByMetric('insurancePayments', this.sumProphy, this.sumPerio, this.sumSrp),
        grossProduction: this.sumByMetric('grossProduction', this.sumProphy, this.sumPerio, this.sumSrp),
        adjustment: this.sumByMetric('adjustment', this.sumProphy, this.sumPerio, this.sumSrp),
        writeoff: this.sumByMetric('writeoff', this.sumProphy, this.sumPerio, this.sumSrp),
        netProduction: this.sumByMetric('netProduction', this.sumProphy, this.sumPerio, this.sumSrp),
      };
    },
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getClinicHygieneReport({ start, end });

      if (result.status === 200) {
        const { prophyDataPerProvider = [], perioDataPerProvider = [], srpDataPerProvider = [] } = result.data;

        this.prophyDataPerProviderAll = prophyDataPerProvider;
        this.perioDataPerProviderAll = perioDataPerProvider;
        this.srpDataPerProviderAll = srpDataPerProvider;
      } else {
        this.prophyDataPerProviderAll = this.perioDataPerProviderAll = this.srpDataPerProviderAll = [];
      }

      this.loading = false;
    },
    formatMoney(number) {
      return Utils.formatCurrency(number, false, false);
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
    calculateNetProduction(grossProduction, adjustment, writeoff) {
      return grossProduction + adjustment - writeoff;
    },
    sumByMetric(metricName, sumProphy, sumPerio, sumSrp) {
      return sumProphy[metricName] + sumPerio[metricName] + sumSrp[metricName];
    },
    sumByProviders(data, propPrefix) {
      let patientPayments = 0;
      let insurancePayments = 0;
      let grossProduction = 0;
      let adjustment = 0;
      let writeoff = 0;
      let netProduction = 0;

      for (const providerData of data) {
        patientPayments += providerData[`${propPrefix}PatientPayments`] ?? 0;
        insurancePayments += providerData[`${propPrefix}InsurancePayments`] ?? 0;
        grossProduction += providerData[`${propPrefix}GrossProduction`] ?? 0;
        adjustment += providerData[`${propPrefix}CompletedProceduresAdjustment`] ?? 0;
        writeoff += providerData[`${propPrefix}EstimatedWriteoff`] ?? 0;

        netProduction += this.calculateNetProduction(
          providerData[`${propPrefix}GrossProduction`] ?? 0,
          providerData[`${propPrefix}CompletedProceduresAdjustment`] ?? 0,
          providerData[`${propPrefix}EstimatedWriteoff`] ?? 0,
        );
      }

      return {
        patientPayments,
        insurancePayments,
        grossProduction,
        adjustment,
        writeoff,
        netProduction,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.hygiene-table {
  :deep(.table-shadow) {
    max-height: 350px;
    overflow: auto;
  }
}
</style>
